// GroupDslDialog.jsx — eigen draggable popup voor het bewerken van één G-veld DSL string
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Tooltip } from '@mui/material';
import JAutoComplete from '../../../../components/shared/JAutoComplete/JAutoComplete';
import JInspectorFooter from '../../../../components/shared/JInspectorComponents/JInspectorFooter';
import useDraggablePanel from '../../../../hooks/useDraggablePanel';
import { tooltipSxElevated } from '../../../../components/shared/jernyTooltipStyling';
import { FIELDS, SORT_FIELDS, parseGroupDsl, serializeGroupDsl } from './groupDsl';
import './GroupDslDialog.css';

const BASE_OPTIONS = ['all', 'pinned', ...FIELDS];

const DEFAULT_POS = () => ({ top: 80, left: Math.max(8, window.innerWidth / 2 - 200) });

export default function GroupDslDialog({ open, gLabel, value, onSave, onClose }) {
    const [parts, setParts] = useState(() => parseGroupDsl(value));
    const { pos, setPos, dragHandleProps } = useDraggablePanel(DEFAULT_POS());

    useEffect(() => {
        if (!open) return;
        setPos(DEFAULT_POS());
        setParts(parseGroupDsl(value));
    }, [open, value]);

    if (!open) return null;

    const update = (patch) => setParts((p) => ({ ...p, ...patch }));

    const handleBaseSelect = (picked) => {
        // JAutoComplete vuurt onChange('') tijdens typen als de tekst geen
        // exacte match is (zoekgedrag) — alleen een echte BASE_OPTIONS match
        // (via klik of Enter) mag de base-state daadwerkelijk wijzigen.
        if (!picked) return;
        if (picked === 'all' || picked === 'pinned') {
            update({ baseType: picked, field: '', fieldValue: '' });
        } else if (FIELDS.includes(picked)) {
            update({ baseType: 'field', field: picked, fieldValue: parts.fieldValue });
        }
    };

    const handleSortPick = (key) => (picked) => {
        if (picked && !SORT_FIELDS.includes(picked)) return;
        update({ [key]: picked });
    };

    const baseDisplay = parts.baseType === 'field' ? parts.field : parts.baseType;
    const composed = serializeGroupDsl(parts);

    const handleSave = () => {
        onSave(composed);
        onClose();
    };

    const panel = (
        <div className="gdd-panel" style={{ top: pos.top, left: pos.left }}>
            <div className="gdd-titlebar" {...dragHandleProps}>
                <span>DSL Editor · {gLabel}</span>
                <div className="gdd-titlebar-actions">
                    <Tooltip title="Save" componentsProps={tooltipSxElevated}>
                        <button className="gdd-titlebar-btn" onClick={handleSave}>
                            <SaveOutlinedIcon sx={{ fontSize: 15 }} />
                        </button>
                    </Tooltip>
                    <Tooltip title="Cancel" componentsProps={tooltipSxElevated}>
                        <button className="gdd-titlebar-btn" onClick={onClose}>
                            <CloseIcon sx={{ fontSize: 15 }} />
                        </button>
                    </Tooltip>
                </div>
            </div>

            <div className="gdd-body">
                <div className="gdd-row">
                    <span className="gdd-label">Base</span>
                    <JAutoComplete
                        id={`gdd-base-${gLabel}`}
                        items={BASE_OPTIONS}
                        value={baseDisplay}
                        onChange={handleBaseSelect}
                        placeholder="all / pinned / Field"
                        className="gdd-base"
                    />
                </div>

                {parts.baseType === 'field' && (
                    <div className="gdd-row">
                        <span className="gdd-label">Value</span>
                        <input
                            className="j-inspector-select"
                            value={parts.fieldValue}
                            onChange={(e) => update({ fieldValue: e.target.value })}
                            placeholder="Value"
                            maxLength={100}
                        />
                    </div>
                )}

                <div className="gdd-section-label">Sort</div>
                <div className="gdd-row">
                    <span className="gdd-label">Asc</span>
                    <JAutoComplete
                        id={`gdd-sortasc-${gLabel}`}
                        items={SORT_FIELDS}
                        value={parts.sortAsc}
                        onChange={handleSortPick('sortAsc')}
                        placeholder="—"
                        className="gdd-sort"
                    />
                </div>
                <div className="gdd-row">
                    <span className="gdd-label">Desc</span>
                    <JAutoComplete
                        id={`gdd-sortdesc-${gLabel}`}
                        items={SORT_FIELDS}
                        value={parts.sortDesc}
                        onChange={handleSortPick('sortDesc')}
                        placeholder="—"
                        className="gdd-sort"
                    />
                </div>

                <div className="gdd-section-label">Preview</div>
                <div className="gdd-row">
                    <input
                        className="j-inspector-select gdd-preview"
                        value={composed}
                        onChange={(e) => setParts(parseGroupDsl(e.target.value))}
                        spellCheck={false}
                    />
                </div>
            </div>

            <JInspectorFooter isDirty onSave={handleSave} onCancel={onClose} />
        </div>
    );

    return createPortal(panel, document.body);
}
